import { Link } from 'react-router-dom';
import BrandMark from '@/components/BrandMark';
import BuiltByFluid from '@/components/BuiltByFluid';

const features = [
  {
    title: 'Personal multisig',
    body: 'Your wallet plus a TOTP-gated co-signer. Both signatures needed — every time.',
  },
  {
    title: 'TOTP-gated signing',
    body: 'Every transaction needs a 6-digit code from Google Authenticator, Authy, or 1Password — bound atomically into the message you sign.',
  },
  {
    title: 'Use any dApp',
    body: 'Connect to Jupiter, Drift, Marginfi, anything via WalletConnect. Your TOTP guards every signature.',
  },
  {
    title: 'Non-custodial',
    body: 'The backend never holds your keys. Funds live in a vault that needs both your wallet and the TOTP-gated co-signer to move.',
  },
  {
    title: 'Recovery codes',
    body: '8 single-use recovery codes generated at setup, encrypted at rest with AES-256-GCM. Lose your phone? You still own your funds.',
  },
  {
    title: 'Open & auditable',
    body: 'Built on open-source, audited on-chain primitives used by treasuries holding 9-figure portfolios.',
  },
];

const steps = [
  'Connect any Solana wallet (Phantom, Solflare, Backpack, Jupiter, Glow)',
  'One-click create your 2-of-2 personal wallet',
  'Set up TOTP in Google Authenticator / Authy / 1Password',
  'Connect any dApp via WalletConnect — every transaction now needs your TOTP',
];

const faqs = [
  {
    q: 'Is this custodial?',
    a: "No. The backend never holds your wallet's key. Your funds live in a vault that requires both your wallet and the TOTP-gated co-signer to move.",
  },
  {
    q: 'What if I lose my phone (TOTP)?',
    a: "Use one of your 8 recovery codes — they're shown once at setup; download them. If both phone and codes are gone, you can't recover funds — that's the trade-off of self-custody.",
  },
  {
    q: 'What does the backend signer actually do?',
    a: "It's a Cloudflare Worker that holds an Ed25519 keypair with Vote-only permission on your wallet. It signs transactions only after a successful TOTP verification. It cannot initiate proposals and cannot execute transactions — only you can.",
  },
  {
    q: 'How is this different from a regular team multisig?',
    a: 'Team multisigs are for groups. This is a personal wallet where the second signer is a TOTP-gated bot you use from any dApp via WalletConnect — like Avocado, for Solana.',
  },
];

export default function Landing() {
  return (
    <div className="relative flex-1 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-grid-faint bg-grid-faint opacity-60" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[60vh] bg-hero-glow" />

      <div className="relative">
        <header className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
          <Link to="/" className="flex items-center">
            <BrandMark variant="full" size="md" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-ink-300">
            <a href="#how" className="hover:text-ink-50 transition-colors">
              How it works
            </a>
            <a href="#features" className="hover:text-ink-50 transition-colors">
              Features
            </a>
            <a href="#faq" className="hover:text-ink-50 transition-colors">
              FAQ
            </a>
          </nav>
          <Link
            to="/app/setup"
            className="rounded-md bg-avo-500 px-4 py-2 text-sm font-semibold text-black hover:bg-avo-600 transition-colors"
          >
            Open app
          </Link>
        </header>

        <section className="max-w-3xl mx-auto px-6 pt-20 pb-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-ink-700 bg-ink-900 px-3 py-1 text-xs text-ink-300 mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-avo-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
            TOTP-gated · on Solana
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.08] bg-gradient-to-b from-white from-30% to-white/55 bg-clip-text text-transparent">
            2FA wallet for Solana.
          </h1>
          <p className="mt-5 text-ink-400 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Your wallet plus a TOTP-gated co-signer. Every transaction needs a
            6-digit code. Non-custodial, works with any dApp.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link
              to="/app/setup"
              className="rounded-md bg-avo-500 px-5 py-2.5 text-sm font-semibold text-black hover:bg-avo-600 transition-colors"
            >
              Get started
            </Link>
            <a
              href="#how"
              className="rounded-md border border-ink-700 bg-ink-900 px-5 py-2.5 text-sm font-medium text-ink-100 hover:border-ink-600 hover:bg-ink-800 transition-colors"
            >
              How it works
            </a>
          </div>
        </section>

        <section id="how" className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl md:text-3xl font-semibold mb-10 text-center">
            How it works
          </h2>
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li
                key={i}
                className="flex gap-4 items-start rounded-lg border border-ink-700 bg-ink-900 px-5 py-4"
              >
                <span className="grid place-content-center w-7 h-7 rounded-full bg-avo-500/15 text-avo-500 font-semibold text-sm shrink-0">
                  {i + 1}
                </span>
                <span className="text-ink-100">{s}</span>
              </li>
            ))}
          </ol>
        </section>

        <section id="features" className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl md:text-3xl font-semibold mb-10 text-center">
            Why this design
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-lg border border-ink-700 bg-ink-900 p-5 hover:border-ink-600 transition-colors"
              >
                <h3 className="font-semibold text-ink-50 mb-2">{f.title}</h3>
                <p className="text-sm text-ink-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="faq" className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl md:text-3xl font-semibold mb-10 text-center">
            FAQ
          </h2>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details
                key={f.q}
                className="rounded-lg border border-ink-700 bg-ink-900 px-5 py-4"
              >
                <summary className="cursor-pointer font-medium text-ink-100">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm text-ink-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <footer className="border-t border-ink-700 mt-16 px-6 py-8 text-sm text-ink-500 flex flex-wrap items-center justify-between gap-3 max-w-6xl mx-auto">
          <span>&copy; {new Date().getFullYear()} Avocado Solana — non-custodial.</span>
          <BuiltByFluid />
        </footer>
      </div>
    </div>
  );
}
